import { uploadAttachments } from 'legacy/lut/services/files/uploadAttachments'
import { useCallback, useMemo, useState } from 'react'

import { AttachFile } from 'clickNgo/components/attachFile/AttachFile'
import { ACCEPTABLE_FILE_TYPES } from 'clickNgo/entities/partner/configs/files'
import { IAttachment } from 'clickNgo/models/file'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { FormCheckBox, useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'

import { PartnerEditorFormKeys, PartnerEditorFormSettings } from '../../../configs'
import { IPartnerEditorData } from '../../../models'
import { partnerEditorTranslation } from '../../../translation'

interface IUseMandatoryAttachmentsFormParams {
  onConfirm?: () => void
  onReject?: () => void
}

enum MandatoryAttachmentsFields {
  HasMandatoryAttachments = 'hasMandatoryAttachments',
  Attachments = 'attachments',
}

export const useMandatoryAttachmentsForm = ({
  onConfirm,
  onReject,
}: IUseMandatoryAttachmentsFormParams) => {
  const [isLoading, setIsLoading] = useState(false)
  const [notUploadedAttachments, setNotUploadedAttachments] = useState<string[]>([])
  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )
  const [isMandatoryAttachmentsDialogVisible, setIsMandatoryAttachmentsDialogVisible] =
    useState(false)
  const [mandatoryAttachments, updateMandatoryAttachments] = useGetFormDataField<IAttachment[]>(
    PartnerEditorFormKeys.MandatoryAttachments,
    MandatoryAttachmentsFields.Attachments
  )
  const [hasMandatoryAttachments, setHasMandatoryAttachments] = useGetFormDataField<boolean>(
    PartnerEditorFormKeys.MandatoryAttachments,
    MandatoryAttachmentsFields.HasMandatoryAttachments
  )

  const renderNonUploadedAttachmentsMessage = useMemo(
    () => <span>{`Some files could not be uploaded: ${notUploadedAttachments.join(', ')}`}</span>,
    [notUploadedAttachments]
  )

  const renderHelpDescription = useMemo(
    () => <Translation text={partnerEditorTranslation.other.mandatoryAttachmentsHelp} />,
    []
  )

  const handleOnMandatoryAttachmentsConfirm = useCallback(async () => {
    setIsLoading(true)

    if (hasMandatoryAttachments) {
      if (onConfirm) {
        onConfirm()
      }
      setHasMandatoryAttachments(false)
      setIsMandatoryAttachmentsDialogVisible(false)
      setIsLoading(false)
      return
    }

    const result = await uploadAttachments({
      partnerId: actualData.partner.partnerId,
      attachments: mandatoryAttachments,
    })

    if (result && result.length === mandatoryAttachments.length) {
      updateActualData({
        ...actualData,
        partnerAttachments: [...actualData.partnerAttachments, ...result],
      })
      if (onConfirm) {
        onConfirm()
      }
      setHasMandatoryAttachments(false)
      setIsMandatoryAttachmentsDialogVisible(false)
      setIsLoading(false)
      return
    } else {
      setNotUploadedAttachments(() =>
        mandatoryAttachments
          .filter((attachment) => {
            return !result.some((newAttachment) => newAttachment.entryId === attachment.entryId)
          })
          .map((attachment) => attachment.fileName)
      )
      updateMandatoryAttachments(result)
      setIsLoading(false)
      return
    }
  }, [
    actualData,
    hasMandatoryAttachments,
    mandatoryAttachments,
    onConfirm,
    setHasMandatoryAttachments,
    updateActualData,
    updateMandatoryAttachments,
  ])

  const handleOnMandatoryAttachmentsReject = useCallback(() => {
    if (onReject) {
      onReject()
    }
    setNotUploadedAttachments([])
    setIsMandatoryAttachmentsDialogVisible(false)
  }, [onReject])

  const handleOnHasMandatoryAttachmentsChange = useCallback(() => {
    setNotUploadedAttachments([])
  }, [])

  const mandatoryAttachmentsContent = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <LoaderOverlay isVisible={isLoading} color="white" />
        <InfoBlock text={renderHelpDescription} type={InfoBlockType.Help} hasIcon hasCloseButton />
        <RenderWhen condition={Boolean(notUploadedAttachments.length)}>
          <InfoBlock
            text={renderNonUploadedAttachmentsMessage}
            type={InfoBlockType.Danger}
            hasIcon
            hasCloseButton
          />
        </RenderWhen>
        <RenderWhen condition={!hasMandatoryAttachments}>
          <AttachFile
            attachments={mandatoryAttachments}
            formKey={PartnerEditorFormKeys.MandatoryAttachments}
            acceptedFormats={ACCEPTABLE_FILE_TYPES}
          />
        </RenderWhen>
        <div style={{ paddingTop: '8px' }}>
          <FormCheckBox
            onChange={handleOnHasMandatoryAttachmentsChange}
            field={MandatoryAttachmentsFields.HasMandatoryAttachments}
            formKey={PartnerEditorFormKeys.MandatoryAttachments}
            defaultValue={false}
            label={partnerEditorTranslation.other.hasMandatoryAttachments}
          />
        </div>
      </div>
    ),
    [
      handleOnHasMandatoryAttachmentsChange,
      hasMandatoryAttachments,
      isLoading,
      mandatoryAttachments,
      notUploadedAttachments.length,
      renderHelpDescription,
      renderNonUploadedAttachmentsMessage,
    ]
  )

  return {
    hasMandatoryAttachments: hasMandatoryAttachments || mandatoryAttachments?.length > 0,
    handleOnMandatoryAttachmentsConfirm,
    handleOnMandatoryAttachmentsReject,
    mandatoryAttachmentsContent,
    isMandatoryAttachmentsDialogVisible,
    setIsMandatoryAttachmentsDialogVisible,
  }
}
